import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Monitor, Smartphone, LogOut, ShieldCheck, ShieldAlert, Trash2, RefreshCw, KeyRound, Clock,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MfaEnroll } from "@/components/MfaEnroll";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const fmt = (v) => (v ? new Date(v).toLocaleString() : "—");
const isMobile = (label) => /iOS|Android|iPhone|iPad/i.test(label || "");

function MfaReauthFields({ google, values, set }) {
  return (
    <div className="space-y-3">
      {!google && (
        <div>
          <label className="text-xs font-medium text-slate-600">Current password</label>
          <input type="password" value={values.password} onChange={(e) => set("password", e.target.value)}
            data-testid="mfa-reauth-password" className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 outline-none focus:ring-2 focus:ring-[#2563EB]" />
        </div>
      )}
      {google && (
        <p className="text-xs text-slate-500">You'll be asked to confirm your Google sign-in, plus a code below.</p>
      )}
      <div>
        <label className="text-xs font-medium text-slate-600">Authenticator or backup code</label>
        <input value={values.code} onChange={(e) => set("code", e.target.value)} placeholder="123456 or backup code"
          data-testid="mfa-reauth-code" className="mt-1 w-full h-10 px-3 rounded-md border border-slate-300 outline-none focus:ring-2 focus:ring-[#2563EB] font-mono tracking-wider" />
      </div>
    </div>
  );
}

export default function SecurityPage() {
  const { user, logout } = useAuth();
  const isGoogle = user?.auth_provider === "google";
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [mfa, setMfa] = useState(null);
  const [activity, setActivity] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [reauth, setReauth] = useState({ password: "", code: "" });
  const [newBackup, setNewBackup] = useState([]);
  const [openDialog, setOpenDialog] = useState(null); // null | "disable" | "regenerate"
  const [reauthToken, setReauthToken] = useState(null); // Google-only purpose-bound token
  const [trusted, setTrusted] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, m, a] = await Promise.all([
        api.get("/auth/sessions"),
        api.get("/auth/mfa/status"),
        api.get("/auth/security-activity"),
      ]);
      setSessions(s.data.sessions || []);
      setMfa(m.data);
      setActivity(a.data);
      if (m.data?.enabled) {
        try { const td = await api.get("/auth/mfa/trusted-devices"); setTrusted(td.data.trusted_devices || []); }
        catch { setTrusted([]); }
      } else { setTrusted([]); }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load security info");
    } finally { setLoading(false); }
  }, []);

  const revokeTrusted = async (id) => {
    setBusy("td-" + id);
    try { await api.delete(`/auth/mfa/trusted-devices/${id}`); toast.success("Trusted device removed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const revokeAllTrusted = async () => {
    setBusy("td-all");
    try { await api.post("/auth/mfa/trusted-devices/revoke-all"); toast.success("All trusted devices removed"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  useEffect(() => { load(); }, [load]);

  // Resume a high-risk action after returning from Google reauthentication.
  useEffect(() => {
    const stored = sessionStorage.getItem("mfa_reauth_token");
    if (!stored) return;
    try {
      const { action, token } = JSON.parse(stored);
      setReauthToken(token);
      setOpenDialog(action === "disable_mfa" ? "disable" : "regenerate");
    } catch { /* ignore malformed */ }
    sessionStorage.removeItem("mfa_reauth_token");
  }, []);

  const startGoogleReauth = (action) => {
    sessionStorage.setItem("mfa_reauth_intent", JSON.stringify({ action, returnTo: "/security" }));
    const redirectUrl = window.location.origin + "/security?reauth=1";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Google-only users must re-authenticate with Google before a high-risk action.
  const onHighRiskAction = (action, dialog) => {
    if (isGoogle && !reauthToken) { startGoogleReauth(action); return; }
    setOpenDialog(dialog);
  };

  const closeDialog = () => { setOpenDialog(null); setReauth({ password: "", code: "" }); setReauthToken(null); };

  const revoke = async (id) => {
    setBusy(id);
    try { await api.delete(`/auth/sessions/${id}`); toast.success("Session signed out"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const logoutOthers = async () => {
    setBusy("others");
    try { const { data } = await api.post("/auth/sessions/logout-all-others"); toast.success(`Signed out ${data.revoked_count} other session(s)`); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const logoutAll = async () => {
    setBusy("all");
    try { await api.post("/auth/sessions/logout-all"); toast.success("Signed out of all devices"); await logout(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(""); }
  };

  const disableMfa = async () => {
    setBusy("mfa-disable");
    try {
      const headers = reauthToken ? { "X-Reauth-Token": reauthToken } : {};
      await api.post("/auth/mfa/disable", { password: reauth.password, code: reauth.code, backup_code: reauth.code }, { headers });
      toast.success("Two-factor authentication disabled");
      closeDialog();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not disable MFA");
    } finally { setBusy(""); }
  };

  const regenBackup = async () => {
    setBusy("mfa-regen");
    try {
      const headers = reauthToken ? { "X-Reauth-Token": reauthToken } : {};
      const { data } = await api.post("/auth/mfa/backup-codes/regenerate", { password: reauth.password, code: reauth.code, backup_code: reauth.code }, { headers });
      setNewBackup(data.backup_codes || []);
      closeDialog();
      toast.success("New backup codes generated");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not regenerate codes");
    } finally { setBusy(""); }
  };

  const downloadBackup = () => {
    const blob = new Blob([`DEI TradeMatch AI — MFA backup codes\n\n${newBackup.join("\n")}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "dei-tradematch-mfa-backup-codes.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  const setRe = (k, v) => setReauth((p) => ({ ...p, [k]: v }));

  return (
    <AppShell title="Security" subtitle="Manage your two-factor authentication and active sessions.">
      <div className="max-w-3xl space-y-6" data-testid="security-page">

        {/* MFA card */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="mfa-card">
          <div className="mb-4 flex items-center gap-2">
            <KeyRound className="text-[#2563EB]" size={20} />
            <h3 className="font-heading font-bold text-[#0A0F1C]">Two-factor authentication</h3>
            {mfa && (
              <span data-testid="mfa-status-badge"
                className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${mfa.enabled ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                {mfa.enabled ? "Enabled" : "Disabled"}
              </span>
            )}
          </div>

          {loading || !mfa ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : mfa.enforcement?.setup_recommended && !mfa.enabled ? (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="mfa-recommended-banner">
              <ShieldAlert size={16} className="mt-0.5" />
              <span>Your role will soon require two-factor authentication. Set it up now to stay ahead of enforcement.</span>
            </div>
          ) : null}

          {!loading && mfa && !mfa.enabled && !enrolling && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">Protect your account with a time-based one-time code from an authenticator app.</p>
              <Button onClick={() => setEnrolling(true)} data-testid="mfa-enable-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8]">
                <ShieldCheck size={16} className="mr-2" /> Enable two-factor authentication
              </Button>
            </div>
          )}

          {!loading && mfa && !mfa.enabled && enrolling && (
            <MfaEnroll onDone={async () => { setEnrolling(false); await load(); }} onCancel={() => setEnrolling(false)} />
          )}

          {!loading && mfa && mfa.enabled && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600">
                <span>Enabled on {fmt(mfa.enrolled_at)}</span>
                <span data-testid="mfa-backup-remaining">{mfa.backup_codes_remaining} backup code(s) left</span>
              </div>

              {newBackup.length > 0 && (
                <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="mfa-new-backup-codes">
                  <p className="text-sm text-amber-800">Save these new codes. Older codes no longer work.</p>
                  <div className="grid grid-cols-2 gap-2 font-mono text-sm">{newBackup.map((c) => <span key={c}>{c}</span>)}</div>
                  <Button size="sm" variant="outline" onClick={downloadBackup} data-testid="mfa-download-new-backup">Download</Button>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {isGoogle && (
                  <p className="w-full text-xs text-slate-500" data-testid="mfa-google-reauth-note">
                    You signed in with Google — you'll re-confirm with Google before disabling or regenerating.
                  </p>
                )}
                {/* Regenerate backup codes */}
                <Button variant="outline" size="sm" data-testid="mfa-regenerate-btn" onClick={() => onHighRiskAction("regenerate_backup_codes", "regenerate")}>
                  <RefreshCw size={14} className="mr-1" /> Regenerate backup codes
                </Button>
                <AlertDialog open={openDialog === "regenerate"} onOpenChange={(o) => !o && closeDialog()}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Regenerate backup codes</AlertDialogTitle>
                      <AlertDialogDescription>Confirm your identity. This invalidates all existing backup codes.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <MfaReauthFields google={isGoogle} values={reauth} set={setRe} />
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="mfa-regenerate-confirm" onClick={regenBackup} disabled={busy === "mfa-regen"} className="bg-[#2563EB] hover:bg-[#1D4ED8]">Regenerate</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Disable MFA */}
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="mfa-disable-btn" onClick={() => onHighRiskAction("disable_mfa", "disable")}>
                  Disable
                </Button>
                <AlertDialog open={openDialog === "disable"} onOpenChange={(o) => !o && closeDialog()}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Disable two-factor authentication?</AlertDialogTitle>
                      <AlertDialogDescription>This lowers your account security. Confirm your identity to continue.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <MfaReauthFields google={isGoogle} values={reauth} set={setRe} />
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="mfa-disable-confirm" onClick={disableMfa} disabled={busy === "mfa-disable"} className="bg-red-600 hover:bg-red-700">Disable MFA</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>

        {/* Trusted devices */}
        {!loading && mfa?.enabled && (
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="trusted-devices-card">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ShieldCheck className="text-[#2563EB]" size={20} />
                <h3 className="font-heading font-bold text-[#0A0F1C]">Trusted devices</h3>
              </div>
              {trusted.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={!!busy} data-testid="trusted-revoke-all-btn">Remove all</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove all trusted devices?</AlertDialogTitle>
                      <AlertDialogDescription>Every device will need two-factor verification on the next sign-in.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction data-testid="trusted-revoke-all-confirm" onClick={revokeAllTrusted}>Remove all</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
            <p className="mb-3 text-sm text-slate-500">Devices you chose to trust skip the 6-digit code for 30 days.</p>
            {trusted.length === 0 ? (
              <div data-testid="trusted-empty" className="py-6 text-center text-sm text-slate-400">No trusted devices.</div>
            ) : (
              <ul className="space-y-2">
                {trusted.map((t) => {
                  const Icon = isMobile(t.label) ? Smartphone : Monitor;
                  return (
                    <li key={t.device_id} data-testid={`trusted-row-${t.device_id}`}
                      className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${t.current ? "border-[#2563EB] bg-blue-50/40" : "border-slate-100 bg-slate-50/60"}`}>
                      <Icon size={20} className="shrink-0 text-slate-500" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#0A0F1C]">{t.label}</span>
                          {t.current && <span className="rounded bg-[#2563EB] px-1.5 py-0.5 text-[10px] font-semibold text-white">This device</span>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
                          <span>{t.location}</span>
                          <span>Last used {fmt(t.last_used_at)}</span>
                          <span>Expires {fmt(t.expires_at)}</span>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" disabled={busy === "td-" + t.device_id}
                        onClick={() => revokeTrusted(t.device_id)} data-testid={`trusted-revoke-${t.device_id}`}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700">
                        {busy === "td-" + t.device_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Recent security activity */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="security-activity-card">
          <div className="mb-4 flex items-center gap-2">
            <Clock className="text-[#2563EB]" size={20} />
            <h3 className="font-heading font-bold text-[#0A0F1C]">Recent activity</h3>
          </div>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4" data-testid="activity-last-login">
                <p className="text-xs font-medium text-slate-400">Last successful login</p>
                <p className="mt-1 text-sm font-medium text-[#0A0F1C]">{fmt(activity?.last_login?.at)}</p>
                {activity?.last_login && (
                  <p className="mt-0.5 text-xs text-slate-500">{activity.last_login.device} · {activity.last_login.location}
                    {activity.last_login.mfa_level && activity.last_login.mfa_level !== "none" ? ` · via ${activity.last_login.mfa_level}` : ""}</p>
                )}
              </div>
              <div className="rounded-lg border border-slate-100 bg-slate-50/60 p-4" data-testid="activity-new-device">
                <p className="text-xs font-medium text-slate-400">Last new device detected</p>
                <p className="mt-1 text-sm font-medium text-[#0A0F1C]">{fmt(activity?.last_new_device?.at)}</p>
                {activity?.last_new_device && (
                  <p className="mt-0.5 text-xs text-slate-500">{activity.last_new_device.device} · {activity.last_new_device.location}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Active sessions */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ShieldCheck className="text-[#2563EB]" size={20} />
              <h3 className="font-heading font-bold text-[#0A0F1C]">Active sessions</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={load} data-testid="sessions-refresh-btn"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={!!busy} data-testid="logout-others-btn"><LogOut size={14} className="mr-1" /> Log out other devices</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Log out all other devices?</AlertDialogTitle>
                    <AlertDialogDescription>This keeps you signed in here but immediately ends every other session.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="logout-others-confirm" onClick={logoutOthers}>Log out others</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-slate-400" /></div>
          ) : sessions.length === 0 ? (
            <div data-testid="sessions-empty" className="py-10 text-center text-sm text-slate-400">No active sessions found.</div>
          ) : (
            <ul className="space-y-2">
              {sessions.map((s) => {
                const Icon = isMobile(s.device_label) ? Smartphone : Monitor;
                return (
                  <li key={s.session_id} data-testid={`session-row-${s.session_id}`}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${s.current ? "border-[#2563EB] bg-blue-50/40" : "border-slate-100 bg-slate-50/60"}`}>
                    <Icon size={20} className="shrink-0 text-slate-500" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-[#0A0F1C]">{s.device_label}</span>
                        {s.current && <span data-testid={`session-current-${s.session_id}`} className="rounded bg-[#2563EB] px-1.5 py-0.5 text-[10px] font-semibold text-white">This device</span>}
                      </div>
                      <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
                        <span>{s.location}</span>
                        <span>Last active {fmt(s.last_seen_at)}</span>
                        <span>Signed in {fmt(s.created_at)}</span>
                      </div>
                    </div>
                    {!s.current && (
                      <Button variant="ghost" size="sm" disabled={busy === s.session_id}
                        onClick={() => revoke(s.session_id)} data-testid={`session-revoke-${s.session_id}`}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700">
                        {busy === s.session_id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-red-200 bg-red-50/50 p-6">
          <h3 className="font-heading font-bold text-red-800">Log out everywhere</h3>
          <p className="mt-1 text-sm text-red-700">Immediately end every session, including this one. You'll need to sign in again.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={!!busy} className="mt-3" data-testid="logout-all-btn"><LogOut size={14} className="mr-1" /> Log out all devices</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Log out of all devices?</AlertDialogTitle>
                <AlertDialogDescription>This ends every session including the one you're using now, and signs you out here.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="logout-all-confirm" onClick={logoutAll} className="bg-red-600 hover:bg-red-700">Log out everywhere</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </AppShell>
  );
}
